import { Component, Inject, OnInit, OnDestroy } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { DirectionService } from "../direction.service";
import { Subscription } from "rxjs";
import { Router } from "@angular/router";
import { DialogService } from "../dialog/dialog.service";
import { DataSharingService } from "../main-section/data-shering-service/data-sharing.service";
import { ClustersService } from "../main-section/choose-branch/clusters.service";
import { firstValueFrom } from "rxjs";
import { AuthService } from "../auth/auth.service";
import { TranslateService } from "@ngx-translate/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { copyTextToClipboard } from 'src/app/utils/clipboard-copy';

@Component({
    templateUrl: './error.component.html',
    styleUrls: ['./error.component.scss'],
    host: {
        class: 'fill-screen-modal'
    }
})

export class ErrorComponent {
    isRTL: boolean = true;
    private directionSubscription: Subscription;
    isDarkMode: boolean = false;
    showSecondStep: boolean = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: { message: string; branchName?: string; details?: string },
        private directionService: DirectionService,
        private dialogRef: MatDialogRef<ErrorComponent>,
        private router: Router,
        private dialogService: DialogService,
        private dataSharingService: DataSharingService,
        private clustersService: ClustersService,
        private authService: AuthService,
        private translateService: TranslateService,
        private snackBar: MatSnackBar,
    ) { }

    ngOnInit() {
        this.directionSubscription = this.directionService.direction$.subscribe(direction => {
            this.isRTL = direction === 'rtl';
        });

        this.directionService.isDarkMode$.subscribe(isDarkMode => {
            this.isDarkMode = isDarkMode;
        });
    }

    ngOnDestroy() {
        if (this.directionSubscription) {
            this.directionSubscription.unsubscribe();
        }
    }

    openWhatsApp() {
        const phoneNumber = '97233746962';
        const message = encodeURIComponent('I-Have-an-Error');
        const url = `https://wa.me/${phoneNumber}?text=${message}`;
        window.open(url, '_blank');
    }

    isNoPrinterFoundError(): boolean {
        return this.data.message === 'NO_PRINTER_FOUND_WITH_THIS_CODE';
    }

    getTranslatedBranchName(): string {
        const serial = this.data.branchName || localStorage.getItem('branch') || '';
        if (!serial) return '';

        const service = localStorage.getItem('printingService') || '';
        const candidates: string[] = [];
        if (service === 'express') {
            candidates.push(`choose-branch.naming.express.${serial}`);
        } else if (service === 'plotter') {
            candidates.push(`choose-branch.naming.plotter.${serial}`);
        }
        candidates.push(`choose-branch.naming.${serial}`);

        for (const key of candidates) {
            const t = this.translateService.instant(key);
            if (t && t !== key) return t;
        }
        return serial;
    }

    hasErrorDetails(): boolean {
        const d = this.data.details;
        return typeof d === 'string' && d.trim().length > 0;
    }

    closeErrorDialog(): void {
        this.dialogRef.close();
    }

    onSwitchBranch(): void {
        // Show second step of the dialog
        this.showSecondStep = true;
    }

    async onChooseDifferentPrinterInBranch(): Promise<void> {
        // Navigate to choose printer page for the same branch
        const branchName = this.data.branchName || localStorage.getItem('branch') || '';
        const printingService = localStorage.getItem('printingService') || '';
        
        if (branchName && printingService) {
            try {
                // Get clusters and find the cluster that contains this branch
                const clusters = await firstValueFrom(this.clustersService.getClusters());
                let clusterName = '';
                
                for (let cluster of clusters) {
                    const foundInPlotter = cluster.plotter?.some(
                        (printer: any) => printer.serial_name === branchName
                    );
                    const foundInExpress = cluster.express?.some(
                        (printer: any) => printer.serial_name === branchName
                    );
                    
                    if (foundInPlotter || foundInExpress) {
                        clusterName = cluster.serial_name;
                        break;
                    }
                }
                
                // Update dataSharingService with required data
                if (clusterName) {
                    this.dataSharingService.setPrintingService(printingService);
                    this.dataSharingService.setBranch(branchName);
                    this.dataSharingService.setCluster(clusterName);
                }
                
                this.dialogRef.close();
                // Close order summary dialog if it's open
                this.dialogService.onCloseOrderSummaryDialog();
                this.router.navigate(['/branch/' + branchName]);
            } catch (error) {
                console.error('Error setting up navigation to choose printer:', error);
                // Fallback: just navigate even if we couldn't set up the data
                this.dialogRef.close();
                this.dialogService.onCloseOrderSummaryDialog();
                this.router.navigate(['/branch/' + branchName]);
            }
        }
    }

    onChooseDifferentBranch(): void {
        // Navigate to choose branch page
        this.dialogRef.close();
        // Close order summary dialog if it's open
        this.dialogService.onCloseOrderSummaryDialog();
        this.router.navigate(['/']);
    }

    onReenterCode(): void {
        // Close dialog without any additional action
        this.closeErrorDialog();
    }

    copyErrorDetails(): void {
        const errorId = this.data.message || 'UNKNOWN_ERROR';
        const userEmail = this.authService.getEmail() || '';
        const currentRoute = this.router.url || '';
        const branchName = this.data.branchName || '';
        
        // Build error details string
        const parts: string[] = [];
        
        // 1. Title + Error ID
        const title = this.translateService.instant('error.copy-error-title');
        parts.push(title + errorId);
        
        // 2. Email (only if exists)
        if (userEmail && userEmail !== '') {
            const emailLabel = this.translateService.instant('log-in-dialog-withmail-email');
            parts.push(`${emailLabel}: ${userEmail}`);
        }
        
        // 3. Route (only if exists and not root)
        if (currentRoute && currentRoute !== '' && currentRoute !== '/') {
            parts.push(`rout: ${currentRoute}`);
        }
        
        // 4. Branch Name (only if exists - relevant for some errors)
        if (branchName && branchName !== '') {
            parts.push(`Branch: ${branchName}`);
        }

        if (this.hasErrorDetails()) {
            parts.push(`${this.translateService.instant('error-details-heading')} ${this.data.details}`);
        }
        
        // Join all parts with " | "
        const errorDetails = parts.join(' | ');
        
        void copyTextToClipboard(errorDetails)
            .then(() => {
                this.snackBar.open(this.translateService.instant('file-issues.copy-success'), '', {
                    duration: 1000,
                    panelClass: ['auto-width', 'center-text'].filter(Boolean),
                });
            })
            .catch((err) => console.error('Could not copy text: ', err));
    }
}